/**
 * apiConnectivity.ts — Eventos cuando vuelve Internet / red en el dispositivo.
 *
 * QUÉ HACE: Emite `altenot:connectivity` y actualiza offlineNoticeStore para el
 * overlay global. Un toast suave al reconectar mejora UX.
 *
 * QUÉ NO HACE: No garantiza que AWS responda; solo avisa que el canal local volvió.
 */

import {
  refreshDeviceOnlineStatus,
  setDeviceOnlineSnapshot,
} from "@/lib/networkStatus";
import { useOfflineNoticeStore } from "@/store/offlineNoticeStore";

const EVENT = "altenot:connectivity";

export type ConnectivityDetail = { connected: boolean };

function emit(connected: boolean): void {
  setDeviceOnlineSnapshot(connected);
  useOfflineNoticeStore.getState().setOnline(connected);
  window.dispatchEvent(
    new CustomEvent<ConnectivityDetail>(EVENT, { detail: { connected } }),
  );
}

let lastToast = 0;

function maybeToastReconnected(): void {
  const now = Date.now();
  if (now - lastToast < 8000) return;
  lastToast = now;
  void import("react-hot-toast").then(({ toast }) => {
    toast.success("Conexión restablecida.", {
      id: "connectivity-back",
      duration: 3500,
    });
  });
}

/** Registra listeners una sola vez (idempotente por flag en window). */
export function installApiConnectivityListeners(): void {
  const w = window as Window & { __altenotConnectivity?: boolean };
  if (w.__altenotConnectivity) return;
  w.__altenotConnectivity = true;

  void refreshDeviceOnlineStatus().then((online) => emit(online));

  window.addEventListener("online", () => {
    emit(true);
    maybeToastReconnected();
  });
  window.addEventListener("offline", () => {
    emit(false);
  });

  const cap = (
    window as unknown as { Capacitor?: { getPlatform?: () => string } }
  ).Capacitor;
  if (cap?.getPlatform?.() === "web") return;

  void import("@capacitor/network").then(({ Network }) => {
    void Network.addListener("networkStatusChange", (s) => {
      emit(s.connected);
      if (s.connected) maybeToastReconnected();
    });
  });
}

export const CONNECTIVITY_EVENT = EVENT;
